import { useRef, useEffect, useState, ReactNode } from 'react'
import { formatAbbreviatedNumber } from '../../utils/numberUtils'
import {
    HELIX_INSIGHT_SIZE_STYLES,
    HelixSearchV2InsightSize,
} from './HelixSearchV2InsightSizeStyles'

interface Props {
    label: ReactNode
    value: number
    subtitle?: ReactNode
    /** Select between 'default', 'compact', or 'micro' views */
    size?: HelixSearchV2InsightSize
}

export default function HelixSearchV2InsightItem({
    label,
    value,
    subtitle,
    size = 'default',
}: Props) {
    const [flash, setFlash] = useState(false)
    const flashTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        setFlash(true)

        if (flashTimeoutRef.current) {
            clearTimeout(flashTimeoutRef.current)
        }

        flashTimeoutRef.current = setTimeout(() => {
            setFlash(false)
            flashTimeoutRef.current = null
        }, 300)
    }, [value])

    const sizeStyles = HELIX_INSIGHT_SIZE_STYLES[size]

    return (
        <div
            className={`${sizeStyles.container} flex flex-col items-center justify-between border rounded-xl bg-white shadow text-center transition duration-300 hover:shadow-md overflow-hidden`}
        >
            <div
                className={`${sizeStyles.label} text-gray-500 font-semibold truncate mb-1 px-2 leading-snug break-words whitespace-normal`}
            >
                {label}
            </div>
            <div className="flex flex-col items-center gap-1">
                <div
                    className={`${
                        sizeStyles.value
                    } font-mono font-bold transition-colors duration-300 ${
                        flash
                            ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#33A86F] via-[#4FCF93] to-[#8BE6B3]'
                            : 'text-transparent bg-clip-text bg-gradient-to-r from-ctxt-blue-dark via-ctxt-blue to-sky-500'
                    }`}
                >
                    {formatAbbreviatedNumber(value)}
                </div>
                {subtitle && (
                    <div className="text-xs text-gray-500 leading-tight whitespace-nowrap">
                        {subtitle}
                    </div>
                )}
            </div>
        </div>
    )
}
